import firebase from '../Firebase/Firebase';
import {
  makePortfolioMediaId,
  PORTFOLIO_MEDIA_SOURCE,
  PORTFOLIO_PHASE,
} from './portfolio';

const MAX_IMAGE_SIZE = 8 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

const safeFileName = (name = 'image') =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase();

export const validateImage = (file) => {
  if (!file) throw new Error('Sélectionnez une image.');
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    throw new Error('Utilisez une image JPG, PNG ou WebP.');
  }
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error('L’image ne doit pas dépasser 8 Mo.');
  }
  return file;
};

const uploadImage = async ({ file, path, onProgress, metadata = {} }) => {
  validateImage(file);
  const task = firebase.storage().ref(path).put(file, {
    contentType: file.type,
    customMetadata: {
      originalName: file.name,
      ...metadata,
    },
  });

  if (onProgress) {
    task.on('state_changed', (snapshot) => {
      const progress = snapshot.totalBytes
        ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        : 0;
      onProgress(progress);
    });
  }

  await task;
  return task.snapshot.ref.getDownloadURL();
};

const requireOwnedCompletedProject = async (demandId) => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Votre session a expiré. Reconnectez-vous.');
  if (!demandId) throw new Error('Le projet est introuvable.');

  const demandRef = firebase.firestore().collection('demands').doc(demandId);
  const snapshot = await demandRef.get();
  if (!snapshot.exists) throw new Error('Le projet est introuvable.');

  const demand = snapshot.data();
  if (demand?.contract?.artisan_id !== user.uid || demand?.demand_status !== 'reviewed') {
    throw new Error('Seul l’artisan de ce projet terminé peut personnaliser sa vitrine.');
  }

  return { user, demandRef, demand };
};

export const uploadProfileImage = async (file, onProgress) => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Votre session a expiré. Reconnectez-vous.');

  const path = [
    'profileImages',
    user.uid,
    `${Date.now()}-${safeFileName(file.name)}`,
  ].join('/');
  const downloadURL = await uploadImage({ file, path, onProgress });

  await firebase.firestore().collection('users').doc(user.uid).set(
    {
      profile_image: downloadURL,
      updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    },
    { merge: true },
  );

  return downloadURL;
};


export const uploadCompanyLogo = async (file, onProgress) => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Votre session a expiré. Reconnectez-vous.');
  if (!['image/jpeg', 'image/png'].includes(file?.type)) {
    throw new Error('Pour les PDF, utilisez un logo PNG ou JPG.');
  }

  const path = [
    'profileImages',
    user.uid,
    `company-logo-${Date.now()}-${safeFileName(file.name)}`,
  ].join('/');

  return uploadImage({
    file,
    path,
    onProgress,
    metadata: {
      purpose: 'artisan-document-logo',
      artisanId: user.uid,
    },
  });
};

export const uploadDemandCoverImage = async (file, demandId, onProgress) => {
  const { user, demandRef } = await requireOwnedCompletedProject(demandId);
  const path = [
    'coverPhotos',
    user.uid,
    demandId,
    `${Date.now()}-${safeFileName(file.name)}`,
  ].join('/');
  const downloadURL = await uploadImage({ file, path, onProgress });

  await demandRef.update({
    demand_cover_photo: downloadURL,
    updated_at: firebase.firestore.FieldValue.serverTimestamp(),
  });

  return downloadURL;
};

export const uploadPortfolioImage = async (file, demandId, onProgress) => {
  const { user } = await requireOwnedCompletedProject(demandId);
  const uniqueName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeFileName(file.name)}`;
  const path = ['portfolio', user.uid, demandId, uniqueName].join('/');
  const downloadURL = await uploadImage({
    file,
    path,
    onProgress,
    metadata: {
      purpose: 'artisan-portfolio',
      artisanId: user.uid,
      demandId,
    },
  });

  return {
    id: makePortfolioMediaId(PORTFOLIO_MEDIA_SOURCE.ARTISAN, downloadURL, uniqueName),
    url: downloadURL,
    source: PORTFOLIO_MEDIA_SOURCE.ARTISAN,
    phase: PORTFOLIO_PHASE.AFTER,
    storage_path: path,
    original_name: file.name,
    uploaded_at: firebase.firestore.Timestamp.now(),
  };
};

export const uploadProjectMessageImages = async (files, demandId, onProgress) => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Votre session a expiré. Reconnectez-vous.');
  if (!demandId) throw new Error('Le projet est introuvable.');

  const selected = Array.from(files || []).slice(0, 4);
  if (!selected.length) return [];
  selected.forEach(validateImage);

  const attachments = [];
  try {
    for (let index = 0; index < selected.length; index += 1) {
      const file = selected[index];
      const uniqueName = `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}-${safeFileName(file.name)}`;
      const path = ['projectMessages', demandId, user.uid, uniqueName].join('/');
      const downloadURL = await uploadImage({
        file,
        path,
        onProgress: (value) => onProgress?.(Math.round(((index + value / 100) / selected.length) * 100)),
        metadata: { purpose: 'project-message', demandId, senderId: user.uid },
      });
      attachments.push({
        url: downloadURL,
        storage_path: path,
        name: file.name,
        content_type: file.type,
        size: file.size,
      });
    }
    return attachments;
  } catch (error) {
    await Promise.all(attachments.map((item) => firebase.storage().ref(item.storage_path).delete().catch(() => undefined)));
    throw error;
  }
};

export const deleteUploadedFiles = async (paths = []) => {
  await Promise.all(Array.from(paths).filter(Boolean).map((path) => (
    firebase.storage().ref(path).delete().catch(() => undefined)
  )));
};
